import classNames from 'classnames';
import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MenuCategory,
  useMenuCategoryGroups,
} from '../../../hooks/useMenuCategoryGroups';
import { getStringLength } from '../../../utils/getStringLengs';
import ChevronDown from '../../../icons/ChevronDown.svg';

import s from './index.scss';
import { useTranslation } from '@wix/wix-i18n-config';

interface IMegaMenuV3Props {
  onCategoryClick: (slug: string) => void;
}

export const MegaMenuV3: FC<IMegaMenuV3Props> = ({ onCategoryClick }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuItemSize, setMenuItemSize] = useState<number>(0);
  const menuCategoryGroups = useMenuCategoryGroups();
  const [openCategoryIndex, setOpenCategoryIndex] = useState<number | null>(
    null,
  );
  const { t } = useTranslation();

  const handleTitleClick = (index: number) => {
    setOpenCategoryIndex(openCategoryIndex === index ? null : index);
  };

  const handleMouseEnter = (index: number) => {
    setOpenCategoryIndex(index);
  };

  const handleMouseLeave = () => {
    setOpenCategoryIndex(null);
  };

  const handleLinkClick = (slug: string) => {
    onCategoryClick(slug);
    setOpenCategoryIndex(null);
  };

  const handleResize = useCallback(() => {
    if (menuRef.current) {
      const width = Math.round(
        menuRef.current.getBoundingClientRect().width - 181,
      );
      setMenuItemSize(width / menuCategoryGroups.length);
    }
  }, [menuCategoryGroups.length]);

  useEffect(() => {
    handleResize();
    globalThis?.window?.addEventListener?.('resize', handleResize);
    return () => {
      globalThis?.window?.removeEventListener?.('resize', handleResize);
    };
  }, [handleResize]);

  // todo: move to scss
  const getMenuWidth = (categories: MenuCategory[]) => {
    const columns = Math.ceil(categories.length / 5);

    if (!menuItemSize) {
      return '-1px';
    }

    const longestItem = Math.ceil(
      Math.max(
        ...categories.map(({ label }) =>
          getStringLength({
            str: label,
            fontSize: 14,
            fontWeight: 500,
          }),
        ),
      ),
    );

    const calculatedWidth = 2 * 24 + (columns - 1) * 60 + columns * longestItem;

    if (calculatedWidth <= menuItemSize) {
      return '-1px';
    }

    // If the title is too long, we need to adjust the width
    if (columns === 1 && calculatedWidth > menuItemSize) {
      return `-${menuItemSize + 1}px`;
    }

    // -1 - shift on one column
    // +1 - border width
    return `-${menuItemSize * (columns - 1) + 1}px`;
  };

  return (
    <div className={s.container} ref={menuRef}>
      <div className={classNames(s.browseBy)}>
        {t('app.topBar.v2.menu.toggle.label')}
      </div>
      <div className={s.menuItems}>
        {menuCategoryGroups.map(({ title, categories }, index) => (
          <div
            key={index}
            className={classNames(s.category, {
              [s.open]: openCategoryIndex === index,
            })}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={classNames(s.titleBtn, {
                [s.active]:
                  openCategoryIndex === index ||
                  categories.find((c) => c.isActive),
              })}
              onClick={() => handleTitleClick(index)}
            >
              <span className={s.title}>{title}</span>
              <ChevronDown className={s.btnIcon} />
            </button>
            <div
              className={classNames(s.listContainer, {
                [s.listContainerFourRows]: categories.length <= 4,
              })}
              style={
                {
                  '--right': getMenuWidth(categories) ?? '-1px',
                } as React.CSSProperties
              }
            >
              <ul
                className={classNames(s.list, {
                  [s.fourRows]: categories.length <= 4,
                })}
              >
                {categories.map(({ label, url, slug, isActive }) => (
                  <li className={s.item} key={slug} data-content={label}>
                    <Link
                      to={url}
                      onClick={() => handleLinkClick(slug)}
                      className={classNames(s.link, { [s.active]: isActive })}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
